
import { mapGetters } from 'vuex';
import optionsData from '@/data/options.json';
import promoData from '@/data/promo.json';
import productData from '@/data/products.json';

export default {
    name: "CourseCard",
    props: {
        productId: { type: String, required: true },
        image: { type: String, required: true },
        alt: { type: String, required: true },
        title: { type: String, required: true },
        subtitle: { type: String, default: 'Learn Online' },
        description: { type: String, required: true },
        badge: { type: String, default: '' },
        dataCy: { type: String, required: true },
        modalId: { type: String, required: true },
        background: { type: String, default: 'bggreen' },
        // Kept for backwards compatibility so parents don't throw prop warnings,
        // but intentionally ignored — region is always resolved from Vuex instead.
        region: { type: String, default: null },
        navigateonclick: { type: Boolean, default: true }
    },
    data() {
        return {
            options: optionsData,
            activeCampaign: null,
            // null until mounted — ensures SSR and client render the same initial HTML
            clientRegion: null,
        }
    },
    computed: {
        ...mapGetters({
            detectedRegion: 'geolocation/region',
            userRegion: 'user/region',
            priceLabel: 'cart/getPriceLabel',
        }),

        // Resolves region from Vuex — userRegion takes priority over detected
        currentRegion() {
            return this.userRegion === 'default' ? this.detectedRegion : this.userRegion
        },

        // Uses clientRegion (set in mounted) so SSR always renders 'en' and avoids hydration mismatches.
        // After mount, updates reactively to the correct Vuex region.
        resolvedRegion() {
            return this.clientRegion || 'en'
        },

        // Normalised region code for promo lookups — handles legacy formats
        normalisedRegion() {
            let rc = this.resolvedRegion.toLowerCase().trim()
            if (rc === 'uk') rc = 'en'
            else if (rc === 'us' || rc === 'en_us') rc = 'en-us'
            return rc
        },

        // Resolve this card's product so we can read its real price/discount
        product() {
            const self = this
            const found = productData.find(function(o) { return o.id.toString() === self.productId })
            return found || {}
        },

        // This card's price data — same source CartPrice uses, so they stay in sync
        priceData() {
            if (this.product && Object.keys(this.product).length > 0) {
                return this.$options.filters.priceData(this.product)
            }
            return {}
        },

        // The course's actual discount %, read from its own price data (not the campaign)
        courseDiscountPercentage() {
            if (this.priceData && this.priceData.pricesDiscounted) {
                return Number(this.$options.filters.discountPercentageFromLabel(this.priceData.pricesDiscounted)) || 0
            }
            return 0
        },

        // True only when the course is genuinely on sale (matches CartPrice's own
        // saleType check) AND discounted beyond 50%
        hasHighDiscount() {
            return this.priceData
                && this.priceData.saleType !== 'none'
                && this.courseDiscountPercentage > 50
        },

        // Show the badge for campaign courses OR any course over 50% off
        showOfferBadge() {
            return this.specialArray.includes(this.productId) || this.hasHighDiscount
        },

        // Campaign courses keep their advertised campaign figure; standalone
        // high-discount courses show their own real computed discount
        badgeOfferAmount() {
            if (this.specialArray.includes(this.productId) && this.SPOfferAmount !== '0') {
                return this.SPOfferAmount
            }
            return Math.round(this.courseDiscountPercentage)
        },

        BFPeriod() {
            const now = new Date();
            const bfStart = new Date('2025-11-24T00:00:00');
            const bfEnd = new Date('2025-11-29T00:00:00');
            return now >= bfStart && now < bfEnd;
        },
        CMPeriod() {
            const now = new Date();
            const cmStart = new Date('2025-11-29T00:00:00');
            const cmEnd = new Date('2025-12-02T00:00:00');
            return now >= cmStart && now < cmEnd;
        },
        promoActive() {
            return this.BFPeriod || this.CMPeriod;
        },

        formattedTitle() {
            const match = this.title.match(/^(\d{2,3}-hour)(.*)/i);
            if (match) {
                const [, hours, rest] = match;
                return '<span class="yellow">' + hours + '</span>' + rest;
            }
            return this.title;
        },
        formattedSubtitle() {
            return this.subtitle.replace(/\|/g, '<span class="yellow">|</span>');
        },

        // Retrieves the specific region data from promoData.json using the normalised region code
        promoRegionData() {
            if (!promoData || !Array.isArray(promoData.regions)) {
                return { standardOffer: {}, specialOffer: {} };
            }

            const self = this;
            let foundRegion = promoData.regions.find(function(r) { return r.code === self.normalisedRegion });

            if (!foundRegion) {
                console.warn('[CourseCard] Region \'' + this.normalisedRegion + '\' not found in promoData. Falling back to \'en\'.');
                foundRegion = promoData.regions.find(function(r) { return r.code === 'en' });
            }

            return foundRegion || { standardOffer: {}, specialOffer: {} };
        },

        activePromoOffer() {
            if (this.activeCampaign) {
                const co = this.activeCampaign.offerForRegion(this.normalisedRegion)
                if (co) return co
            }
            if (this.promoRegionData.specialOffer &&
                this.promoRegionData.specialOffer.enabled &&
                Array.isArray(this.promoRegionData.specialOffer.coursesOnSale) &&
                this.promoRegionData.specialOffer.coursesOnSale.length > 0) {
                return this.promoRegionData.specialOffer
            }
            return this.promoRegionData.standardOffer || {}
        },

        specialArray() {
            if (this.activeCampaign) {
                const co = this.activeCampaign.offerForRegion(this.normalisedRegion)
                if (co && Array.isArray(co.coursesOnSale)) return co.coursesOnSale
            }
            const specialOffer = this.promoRegionData.specialOffer
            if (specialOffer && specialOffer.enabled && Array.isArray(specialOffer.coursesOnSale)) {
                return specialOffer.coursesOnSale
            }
            return []
        },

        saleArray() {
            return this.options.freeproduct_array ? JSON.parse(this.options.freeproduct_array) : [];
        },

        SPOfferAmount() {
            if (this.activePromoOffer && typeof this.activePromoOffer.discountAmount !== 'undefined') {
                return this.activePromoOffer.discountAmount;
            }
            return '0';
        },
    },
    mounted() {
        // Set clientRegion here so SSR and initial client render both use 'en',
        // then Vue reactively updates to the correct region after hydration.
        this.clientRegion = this.currentRegion

        if (this.$campaign && this.$campaign.isActive) {
            const regionOffer = this.$campaign._offersMap && this.$campaign._offersMap[this.normalisedRegion]
            if (regionOffer && Array.isArray(regionOffer.coursesOnSale) && regionOffer.coursesOnSale.length > 0) {
                this.activeCampaign = this.$campaign
            }
        }
    },

    methods: {
        // Navigates to the course page using the resolved region
        navigate() {
            window.location.href = this.$regionUrl(this.productId, this.resolvedRegion);
        },
        updateModal(id) {
            this.$store.dispatch('pages/updateActiveModal', id);
        }
    }
}
